import type { DropdownSelectOption } from '@paintscout/ui-business'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { getRangePresetValue } from '@paintscout/util/util'
import moment from 'moment'

export interface DashboardState {
  quoteDateMin: number
  quoteDateMax: number
  rangePresetKey: string
  estimators: { value: string; label: string }[]
  archived: boolean
  quoteTypes: string[]
  selectedLocations: DropdownSelectOption[]
}

/**
 * Pre-#15866 key. It has not been *written* since v6.15, but it was still read as a fallback and
 * whitelisted from every logout — so a filter set under one company was adopted by the next company
 * logged into on that browser, then re-persisted under that company's key. That stranded a foreign
 * `owner.id` in the quoteSummary query, which can never match, zeroing every stat card (#22045).
 * We only reference it now to delete it.
 */
export const LEGACY_DASHBOARD_OPTIONS_KEY = 'dashboardOptions'

export const getDashboardOptionsKey = (clientId: string) => `${LEGACY_DASHBOARD_OPTIONS_KEY}-${clientId}`

export function getDefaultDashboardState(): DashboardState {
  return {
    quoteDateMin: moment().subtract(1, 'months').startOf('day').unix() * 1000,
    quoteDateMax: moment().endOf('day').unix() * 1000,
    rangePresetKey: 'lastThirtyDays',
    estimators: [],
    archived: undefined,
    quoteTypes: [],
    selectedLocations: []
  }
}

/**
 * localStorage throws rather than returning null in a few environments (Safari private browsing,
 * storage disabled). The dashboard state is read from a `useState` initializer, so anything thrown
 * here white-screens the whole page — swallow and fall back to defaults instead.
 */
function readStoredState(key: string): Partial<DashboardState> | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (err) {
    // Distinguish storage-access errors (e.g. SecurityError in private browsing) from a corrupt
    // stored value so a silently-reset filter bar is diagnosable in the field.
    // eslint-disable-next-line no-console
    console.warn('[dashboardState] Failed to read stored dashboard filters from localStorage', err)
    return null
  }
}

function removeStoredState(key: string) {
  try {
    window.localStorage.removeItem(key)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[dashboardState] Failed to remove stored dashboard filters from localStorage', err)
  }
}

/**
 * Load the persisted dashboard filter bar for `clientId`.
 *
 * Reads *only* the company-scoped key, and deletes the legacy un-scoped one on the way through so
 * the stale cross-company blob is cleaned up without waiting for a logout (#22045).
 */
export function loadDashboardState(clientId: string): DashboardState {
  removeStoredState(LEGACY_DASHBOARD_OPTIONS_KEY)

  const savedState = readStoredState(getDashboardOptionsKey(clientId))
  if (!savedState) return getDefaultDashboardState()

  let quoteDateMin = savedState.quoteDateMin
  let quoteDateMax = savedState.quoteDateMax

  /*
    If there is a selected rangePresetKey, we don't actually want to use the
    stored quoteDateMin/quoteDateMax... But rather, we'll want to re-calculate
    it each time we render (so that we end up with the most current results)
  */
  if (savedState.rangePresetKey) {
    const selected = getRangePresetValue(savedState.rangePresetKey)
    if (selected) {
      quoteDateMin = selected.from.unix() * 1000
      quoteDateMax = selected.to.unix() * 1000
    }
  }

  // estimators used to be stored as bare id strings — drop those, they can't render a chip
  const cleanStateEstimators = (savedState.estimators ?? []).filter((estimator) => typeof estimator === 'object')

  // Built field-by-field rather than spreading `savedState`: the stored blob is untrusted JSON, so
  // a `null` array (which survives JSON round-tripping, unlike `undefined`) would otherwise land in
  // state and blow up the query builders on `.length`. Also drops unknown legacy keys.
  return {
    quoteDateMin,
    quoteDateMax,
    rangePresetKey: savedState.rangePresetKey,
    estimators: cleanStateEstimators,
    archived: savedState.archived,
    quoteTypes: savedState.quoteTypes ?? [],
    selectedLocations: savedState.selectedLocations ?? []
  }
}

/**
 * Drop persisted estimators that don't resolve to a user in the current company.
 *
 * `EstimatorSelect` only renders a chip for a value it finds in its fetched options, so an id left
 * behind by another company (or a deleted user) is applied to every dashboard query while the
 * dropdown still reads "Any estimator" — invisible, with no way to clear it (#22045). Reconciling against the
 * fetched list lets a poisoned browser heal itself on load.
 *
 * A *deactivated* user in the same company is deliberately kept: `EstimatorSelect` keeps
 * selected-but-deactivated users in its options, so that chip is visible and the user can clear it.
 *
 * Returns `null` when there is nothing to change, so callers can bail without a setState loop.
 */
export function reconcileEstimators<T extends { value: string }>(
  estimators: T[],
  knownEstimators: Array<{ value: string }>
): T[] | null {
  // no filter saved, or the estimator list hasn't loaded yet — never wipe a legitimate filter mid-fetch
  if (!estimators?.length || !knownEstimators?.length) return null

  // stored values carry the `auth0|` prefix, query values don't — normalize both sides
  const known = new Set(knownEstimators.map((estimator) => removeAuth0Prefix(estimator.value)))
  const kept = estimators.filter((estimator) => known.has(removeAuth0Prefix(estimator.value)))

  return kept.length === estimators.length ? null : kept
}
